import { API_BASE } from '../config';
import { calculateFinalPrice } from '../pages/calendar/utils';
import { dniPlatne, dniPlatneZapisanej } from './dniPlatne';
import { parseAmount } from './amountField';

// Helpery dat
export const formatYMD = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
};

export const parseYMD = (s) => {
  if (!s) return null;
  if (s instanceof Date) return s;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const round2 = (v) => Number((Number(v || 0)).toFixed(2));

// Adres firmy z Danych firmy: ulica, kod i miasto, a bez nich dawna kolumna `company_address`.
// LUSTRO Login/backend/lib/adresFirmy.js (umowa, podgląd PDF, faktura). Ekran Danych firmy nie ma
// pola pełnego adresu, więc podgląd konstruktora czytający samo `company_address` pokazywał pusty
// {{organizationAddress}} każdej firmie, która wpisała dane w panelu (test odbiorczy 11.10.2026).
const czystyTekst = (v) => (v === undefined || v === null ? '' : String(v).replace(/\s+/g, ' ').trim());
// „Firma: X, NIP: Y” dla klienta firmowego, pusty tekst dla prywatnego. LUSTRO
// Login/backend/lib/contractHtml.js#liniaFirmyKlienta (zmienna {{clientCompanyLine}}).
export function liniaFirmyKlienta(nazwa, nip) {
  const n = czystyTekst(nazwa);
  const p = czystyTekst(nip);
  return [n ? `Firma: ${n}` : '', p ? `NIP: ${p}` : ''].filter(Boolean).join(', ');
}

// Pola składają adres tylko wtedy, gdy jest wśród nich ulica; bez ulicy wygrywa pełny adres ze
// starej kolumny (samo dopisane miasto wypierało go, przegląd kodu 12.10.2026).
export function adresFirmy(settings) {
  const s = settings || {};
  const ulica = czystyTekst(s.company_street);
  const kodIMiasto = [czystyTekst(s.company_postal_code), czystyTekst(s.company_city)].filter(Boolean).join(' ');
  const zPol = [ulica, kodIMiasto].filter(Boolean).join(', ');
  if (ulica) return zPol;
  return czystyTekst(s.company_address) || zPol;
}

// Liczba albo null, gdy wartości NIE MA. Zero jest kwotą (rabat 100%), nie brakiem.
const kwotaLubBrak = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Zapas, gdy wywołujący nie podał wyceny: `total_price` rezerwacji jest ZAWSZE przed
// rabatem, więc rabat odejmujemy tą samą regułą co lista kalendarza. W edycji rabat
// bierzemy z formularza, bo to on trafi do zapisu.
function kwotaZRezerwacji({ editing, editForm, reservation }) {
  const przedRabatem = kwotaLubBrak(reservation?.total_price);
  if (przedRabatem === null) return { total: 0, discount: 0 };
  const zrodloRabatu = editing
    ? { discount_value: editForm?.discount_value, discount_unit: editForm?.discount_unit }
    : reservation;
  const total = calculateFinalPrice({ ...zrodloRabatu, total_price: przedRabatem });
  return { total, discount: Math.max(0, przedRabatem - total) };
}

const MS_PER_DAY = 86400000;
const startOfDay = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : parseYMD(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
};
const daysInclusive = (from, to) => {
  const f = startOfDay(from);
  const t = startOfDay(to);
  if (!f || !t) return 0;
  const diff = Math.round((t - f)/MS_PER_DAY);
  return diff >= 0 ? diff + 1 : 0;
};

const getEquipmentDailyRate = (equipment, isLongTermRental = false) => {
  if (!equipment) return 0;
  const regular = Number(
    equipment.price_per_day ??
    equipment.price ??
    equipment.cena ??
    equipment.pricePerDay ??
    0
  ) || 0;
  const longTerm = Number(
    equipment.price_over_7 ??
    equipment.priceOver7 ??
    equipment.long_term_price ??
    equipment.cena7plus ??
    0
  ) || 0;
  return isLongTermRental && longTerm > 0 ? longTerm : regular;
};

// Mapowanie sprzętu (upewniamy się że pola istnieją w formie użytecznej dla backendu)
function mapEquipmentToContractItems(items = [], options = {}) {
  const { isLongTermRental = false } = options;
  return items.map(eq => {
    if (!eq) return {};
    const priceNum = getEquipmentDailyRate(eq, isLongTermRental);
    const bailNum = Number(eq.bail ?? 0) || 0;
    const inventory =
      eq.inventory_number ??
      eq.inventoryNumber ??
      eq.inventory ??
      eq.sprzetID ??
      eq.nr ??
      eq.id ?? '';

    return {
      // Pola używane przez server.js (eqField)
      kategoria: eq.category ?? eq.kategoria ?? '',
      marka: eq.brand ?? eq.marka ?? '',
      sprzetID: inventory,
      rozmiar: eq.size ?? eq.rozmiar ?? '',
      cena: priceNum.toFixed(2),        // czysta liczba jako string (serwer wyciągnie)
      price_per_day: priceNum,          // jawnie liczba (fallback)
      bail: bailNum,                    // kaucja dla pozycji (jeśli kiedyś potrzebne)
      // Dodatkowe informacyjne
      nazwa: eq.name ?? '',
      klasa: eq.class ?? '',
      nr: inventory,
      price_over_7: Number(eq.price_over_7 ?? 0) || 0
    };
  });
}

// DODANE: wspólna funkcja wyliczenia kaucji (override > zapisane override > pricing/display > wyliczona z bail)
function resolveDeposit({
  editing,
  editForm,
  reservation,
  pricing,
  displayValues,
  totalBailFallback
}) {
  // surowy override z edycji (obu nazw używanych w różnych miejscach)
  const rawEditOverride =
    editForm &&
    (editForm.deposit_override_raw ?? editForm.deposit_override ?? '');

  // Pole kaucji trzyma SUROWY TEKST z polskim przecinkiem („150,00”, prefill z
  // ReservationFormView#kwotaDoPola). Number('150,00') daje NaN, więc umowa z edycji miała
  // kaucję 0 zł, choć okno pokazywało 150 zł (przegląd PR #214). parseAmount czyta oba zapisy.
  if (editing && rawEditOverride !== '' && rawEditOverride != null) {
    const kwota = parseAmount(rawEditOverride);
    return kwota !== null && kwota > 0 ? kwota : 0;
  }

  // zapisane w rezerwacji
  if (!editing && reservation?.deposit_override != null) {
    return Number(reservation.deposit_override) || 0;
  }

  // pricing / DISPLAY_VALUES
  const fromPricing = editing ? (pricing?.deposit ?? 0) : (displayValues?.deposit ?? 0);
  if (fromPricing) return Number(fromPricing) || 0;

  // fallback z bail (jeśli przekazano)
  if (totalBailFallback != null) return Number(totalBailFallback) || 0;

  // dodatkowy fallback na reservation.deposit
  if (reservation?.deposit != null) return Number(reservation.deposit) || 0;

  return 0;
}

// GŁÓWNA funkcja używana w calendar.js
export function buildContractDataFromCalendar({
  isEditing,
  editForm,
  reservation,
  availableEquipment,
  detailEquipment,
  pricing,
  DISPLAY_VALUES,
  tabelaDniPlatnych = null
}) {
  const editing = !!isEditing;

  const firstName = editing ? (editForm.firstname || '') : (reservation.firstname || '');
  const lastName  = editing ? (editForm.lastname || '')  : (reservation.lastname  || '');
  const phone     = editing ? (editForm.phone || '')     : (reservation.phone || reservation.phone_number || '');
  const email     = editing ? (editForm.email || '')     : (reservation.email || '');

  const addrSrc = editing ? editForm : reservation;
  const addrParts = [];
  if (addrSrc.street) addrParts.push(addrSrc.street);
  if (addrSrc.building_number) addrParts.push(
    addrSrc.apartment_number
      ? `${addrSrc.building_number}/${addrSrc.apartment_number}`
      : addrSrc.building_number
  );
  const pcCity = [addrSrc.postal_code, addrSrc.city].filter(Boolean).join(' ');
  if (pcCity) addrParts.push(pcCity);
  const address = addrParts.join(', ');

  const startDate = editing
    ? (editForm.start_date ? formatYMD(editForm.start_date) : null)
    : formatYMD(parseYMD(reservation.start_date));
  const endDate = editing
    ? (editForm.end_date ? formatYMD(editForm.end_date) : null)
    : formatYMD(parseYMD(reservation.end_date));

  // Dni
  let totalDays = editing ? (pricing?.days ?? 0) : (DISPLAY_VALUES?.days ?? 0);
  if (!totalDays) totalDays = daysInclusive(startDate, endDate);
  const isLongTermRental = totalDays > 7;

  // Sprzęt
  let items = [];
  if (editing) {
    const ids = Array.isArray(editForm.equipment_ids) ? editForm.equipment_ids : [];
    items = (availableEquipment || []).filter(eq => ids.includes(eq.id));
  } else if (Array.isArray(detailEquipment) && detailEquipment.length) {
    items = detailEquipment;
  } else if (reservation.equipment_names && reservation.equipment_names !== 'Brak przypisanego sprzętu') {
    items = reservation.equipment_names.split(',').map(n => ({ name: n.trim() }));
  }
  const selectedEquipment = mapEquipmentToContractItems(items, { isLongTermRental });

  // Nowa wycena w edycji: `pricing.billableDays` niesie dni płatne z wyceny serwera. Zapisana kwota
  // dzieli się na dni tak, jak ją policzono (utils/dniPlatne.js#dniPlatneZapisanej), bo dzisiejsza tabela
  // firmy nie może zmienić dokumentu starej rezerwacji. Serwer dopasowuje wiersze umowy do ceny
  // (lib/offerRows.js#dopasujDoCeny), więc dni decydują tu tylko o podziale na dni i stawkę.
  const wycena = editing ? pricing : DISPLAY_VALUES;
  const billableDays = editing && !pricing?.savedAmount
    ? (pricing?.billableDays ?? dniPlatne(totalDays, tabelaDniPlatnych))
    : dniPlatneZapisanej(totalDays, tabelaDniPlatnych, wycena?.subtotal, selectedEquipment.map((eq) => eq.price_per_day));

  // Wyliczony bail sprzętu dla fallbacku (jednokrotna suma, bez mnożenia przez dni)
  const totalBailFallback = selectedEquipment.reduce(
    (s, eq) => s + (Number(eq.bail || 0) || 0),
    0
  );
  
  // Kaucja z override
  const finalDeposit = resolveDeposit({
    editing,
    editForm,
    reservation,
    pricing,
    displayValues: DISPLAY_VALUES,
    totalBailFallback
  });

  // Kwota końcowa (po rabacie) i rabat w zł z tej samej wyceny, którą pokazuje okno.
  // Dawniej `!totalPrice` brało 0 za brak kwoty, więc przy rabacie 100% umowa drukowała
  // kwotę sprzed rabatu jako „do zapłaty”. Zapas tylko, gdy wyceny naprawdę nie ma.
  let totalPrice = kwotaLubBrak(wycena?.total);
  let discountAmount = kwotaLubBrak(wycena?.discountAmount);
  if (totalPrice === null) {
    const zapas = kwotaZRezerwacji({ editing, editForm, reservation });
    totalPrice = zapas.total;
    if (discountAmount === null) discountAmount = zapas.discount;
  }
  if (discountAmount === null) discountAmount = 0;

  // Zaliczka
  const advanceAmount = editing
    ? Number(editForm.advance_amount || 0)
    : Number(reservation.advance_amount || 0);
  const advancePaid = editing
    ? !!editForm.advance_paid
    : !!reservation.advance_paid;

  const cenaCalosc = round2(totalPrice);
  const wplaconaZaliczka = advancePaid ? round2(advanceAmount) : 0;
  const doZaplaty = round2(Math.max(0, cenaCalosc - wplaconaZaliczka));

  // Rabat (kwota w zł policzona wyżej)
  const discountUnit = editing
    ? (editForm.discount_unit || '%')
    : (reservation.discount_unit || '%');

  const discount = {
    amount: round2(discountAmount),
    unit: discountUnit,
    valueZloty: round2(discountAmount)
  };

  // Tag do szablonu umowy: wartość rabatu w PLN
  const rabatPLN = round2(discount.valueZloty);

  // Tabela sprzętu (jeśli szablon jej używa)
  const equip = selectedEquipment.map((it, idx) => {
    const daily = Number(it.price_per_day ?? it.cena ?? 0) || 0;
    const liczbaDni = billableDays;
    return {
      lp: idx + 1,
      rodzajSprzet: it.kategoria || it.category || '',
      marka: it.marka || it.brand || '',
      sprzetID: it.sprzetID || it.nr || it.inventory_number || it.id || '',
      rozmiar: it.rozmiar || it.size || '',
      ilosc: '1',
      cenaDzien: daily.toFixed(2),
      liczbaDni,
      cenaLaczna: (daily * liczbaDni).toFixed(2)
    };
  });

  return {
    firstName,
    lastName,
    phone,
    email,
    address,
    idNumber: reservation.id_number || '',
    clientCompanyName: reservation.company_name || '',
    clientNip: reservation.company_nip || '',
    startDate,
    endDate,
    selectedEquipment,
    equip,
    days: totalDays,
    billableDays,
    totalPrice: cenaCalosc,
    deposit: round2(finalDeposit),
    discount,
    rabatPLN,
    // alias dla starszych/alternatywnych szablonów
    rabat: rabatPLN,
    cenaCalosc,
    kaucja: round2(finalDeposit),
    wplaconaZaliczka,
    doZaplaty,
    advanceAmount: round2(advanceAmount),
    advancePaid,
    advance_amount: round2(advanceAmount),
    advance_paid: advancePaid,
    // Nowe tagi dla szablonu umowy
    nrTelefonu: phone || '',
    notatki: editing
      ? (editForm?.additional_note ?? editForm?.note ?? '')
      : (reservation?.additional_note ?? reservation?.note ?? ''),
    deposit_override: (editing
      ? (editForm?.deposit_override_raw ?? editForm?.deposit_override) !== '' &&
        (editForm?.deposit_override_raw ?? editForm?.deposit_override) != null
      : reservation?.deposit_override != null)
      ? round2(finalDeposit)
      : null
  };
}

// Formularz rezerwacji (nowa rezerwacja)
export function buildContractDataFromReservation({
  form,
  dateFrom,
  dateTo,
  selectedEquipmentIds,
  equipmentList,
  days,
  chargeableDays,
  toPay,
  totalBail,
  discountValue
}) {
  const eqItems = (selectedEquipmentIds || [])
    .map(id => (equipmentList || []).find(eq => eq.id === id))
    .filter(Boolean);
  const isLongTermRental = days > 7;
  const selectedEquipment = mapEquipmentToContractItems(eqItems, { isLongTermRental });

  const cenaCalosc = round2(toPay);
  const wplaconaZaliczka = form.advancePaid ? round2(form.advanceAmount) : 0;
  const doZaplaty = round2(Math.max(0, cenaCalosc - wplaconaZaliczka));

  // Override kaucji w formularzu
  const finalDeposit = resolveDeposit({
    editing: true,
    editForm: { deposit_override_raw: form.depositOverrideRaw },
    reservation: null,
    pricing: { deposit: totalBail },
    displayValues: null,
    totalBailFallback: totalBail
  });

  return {
    firstName: form.firstname,
    lastName: form.lastname,
    phone: form.phone,
    email: form.email,
    address: `${form.street || ''} ${form.building_number || ''}${form.apartment_number?'/'+form.apartment_number:''}, ${form.postal_code || ''} ${form.city || ''}`.trim(),
    idNumber: form.idNumber,
    // Celowo BEZ clientCompanyName/clientNip: ten formularz nie zna danych firmy klienta, więc
    // serwer dobiera je po e-mailu, tak jak zapis rezerwacji łączy klienta (11.10.2026). Pusty
    // klucz znaczyłby „klient prywatny” i wyłączał to dobieranie.
    startDate: dateFrom ? formatYMD(dateFrom) : null,
    endDate: dateTo ? formatYMD(dateTo) : null,
    selectedEquipment,
    totalPrice: cenaCalosc,
    deposit: round2(finalDeposit),
    days,
    billableDays: chargeableDays,
    discount: {
      amount: Number(form.discount || 0),
      unit: form.discountUnit || '%',
      valueZloty: round2(discountValue || 0)
    },
    rabatPLN: round2(discountValue || 0),
    // alias dla starszych/alternatywnych szablonów
    rabat: round2(discountValue || 0),
    cenaCalosc,
    kaucja: round2(finalDeposit),
    wplaconaZaliczka,
    doZaplaty,
    advanceAmount: round2(form.advanceAmount || 0),
    advancePaid: !!form.advancePaid,
    advance_amount: round2(form.advanceAmount || 0),
    advance_paid: !!form.advancePaid,
    // Nowe tagi dla szablonu umowy
    nrTelefonu: form.phone || '',
    notatki: form.note || '',
    deposit_override: form.depositOverrideRaw !== '' && form.depositOverrideRaw != null
      ? round2(finalDeposit)
      : null
  };
}

// Wysłanie żądania generacji
// Treść błędu z /api/generate-contract do pokazania w panelu, zawsze jako zdanie, nigdy surowy
// JSON. Zdanie `message` od serwera (503 contract_pdf_failed) idzie bez zmian; resztę opisuje
// panel po statusie: sesja (401 albo csrf_invalid), za duże żądanie, przeciążenie, brak szablonu,
// błąd serwera. Dawniej każda odpowiedź z polem `error` lądowała w komunikacie jako
// „Błąd generowania umowy: {"error":"csrf_invalid"}” (test odbiorczy 11.10.2026).
// `t` to funkcja tłumaczeń panelu; bez niej wynikiem jest klucz komunikatu.
export function contractErrorMessage(txt, status, tlumacz) {
  const t = typeof tlumacz === 'function' ? tlumacz : (klucz) => klucz;
  let json = null;
  try { json = txt ? JSON.parse(txt) : null; } catch (_) { json = null; }
  if (json && typeof json.message === 'string' && json.message.trim()) return json.message.trim();
  const kod = json && typeof json.error === 'string' ? json.error : '';
  if (!status) return t('contractError.network');
  if (status === 401 || kod === 'csrf_invalid') return t('contractError.session');
  if (status === 413) return t('contractError.tooLarge');
  if (status === 429 || status === 503) return t('contractError.busy');
  if (status === 404) return t('contractError.noTemplate');
  if (status >= 500) return t('contractError.server');
  return t('contractError.http', { status });
}

export async function sendAndDownloadContract(contractData, { t } = {}) {
  const csrf = localStorage.getItem('csrfToken') || '';
  let response;
  try {
    response = await fetch(`${API_BASE}/api/generate-contract`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': csrf
      },
      body: JSON.stringify(contractData)
    });
  } catch (_) {
    // Brak połączenia: zdanie zamiast angielskiego „Failed to fetch” z przeglądarki.
    throw new Error(contractErrorMessage('', 0, t));
  }

  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(contractErrorMessage(txt, response.status, t));
  }

  const blob = await response.blob();
  // Backend zwraca PDF gdy org ma szablon konstruktora (K.2), inaczej .docx (fallback).
  // Dobierz rozszerzenie wg faktycznego typu odpowiedzi, nie na sztywno.
  const contentType = response.headers.get('content-type') || '';
  const ext = contentType.includes('pdf') ? 'pdf' : 'docx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const today = new Date().toLocaleDateString('pl-PL').replace(/\./g,'-');
  const ln = (contractData.lastName || '').trim();
  const fn = (contractData.firstName || '').trim();
  const namePart = [ln, fn].filter(Boolean).join('-');
  a.download = `${namePart}-${today}-umowa.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}